import { BackendAdapterBragg } from "@spawn/backend-adapter-bragg";
import { consumer, inject } from "@spawn/core";
import { FreegameSpinActionBragg, IBraggResponseFreegameEvent } from "@spawn/freegames";
import { PaylineType } from "@spawn/networking";
import {
    BraggSlotfaceRestoreService,
    ILevelDataBragg,
    ensurePaddingMysteries,
    parseRevealBraggNetworkState,
    replaceRevealPositionsWithMysteries,
    RevealUncoverTier,
} from "@spawn/reveal";
import { NudgeUpService } from "../../services/nudgeUpService";
import { BaseSymbolTypes, SymbolService, symbolTypeBraggIds } from "../../services/symbolService";
import { deepClone } from "../../utils/object";
import { getWonFGScatterCount } from "../helpers";
import { IGameNetworkState } from "../networkState";
import { INrgBraggSlotOriginal, NrgBraggFreegameResult } from "../types/bragg";
import { ensure4BlockMysteries, nrgGenerateBraggNudgingOffsets } from "./helpers";
import { UncoverTierService } from "../../services/uncoverTierService";

@consumer
export class FGSpinActionBragg extends FreegameSpinActionBragg {
	@inject protected readonly uncoverTierService!: UncoverTierService;
	@inject protected readonly braggSlotfaceRestoreService!: BraggSlotfaceRestoreService;
	@inject protected readonly nudgeUpService!: NudgeUpService;
	@inject protected readonly symbolService!: SymbolService;

	protected override parse(response: IBraggResponseFreegameEvent, adapter: BackendAdapterBragg): IGameNetworkState {
		const result = super.parse(response, adapter);

		const { data } = response.body as NrgBraggFreegameResult;
		const slotOriginal = data.SLOT_ORIGINAL;

		const reveals = data.FREE_SPIN.reveals;

		const braggSlotface = deepClone(data.FREE_SPIN.slotFace);
		for (const reel of braggSlotface) {
			reel.shift();
			reel.pop();
		}

		this.braggSlotfaceRestoreService.setLevelData({
			level: String(result.slotGameBase.selectedBet),
			slotFace: braggSlotface,
			reveals: reveals ?? [],
		});
		const uncoverTier = this.uncoverTierService.fromWinFactor(result.slotGameBase.winFactor);

		const feature: Partial<IGameNetworkState> = {};
		feature.fgTriggerState = this.extractFGTriggerState(slotOriginal, result.slotGameBase.selectedBet, uncoverTier);

		if (reveals?.length) {
			ensurePaddingMysteries(result.slotGameBase.reels, reveals, BaseSymbolTypes.Mystery);
			replaceRevealPositionsWithMysteries(result.slotGameBase.reels, reveals, BaseSymbolTypes.Mystery);

			feature.reveal = parseRevealBraggNetworkState(reveals, symbolTypeBraggIds, uncoverTier);
			feature.nudging = nrgGenerateBraggNudgingOffsets(reveals);

			if (feature.nudging.some((nudge) => nudge < 0)) {
				ensure4BlockMysteries(result.slotGameBase.reels);
			}
		} else {
			feature.reveal = {
				symbols: [],
			};
			feature.nudging = [];
		}

		this.symbolService.replaceSwingingMysteriesIfNotNeeded(result.slotGameBase.reels);

		if (getWonFGScatterCount(feature.reveal) > 0) {
			feature.preNudgeUpReels = result.slotGameBase.reels;
		}

		return {
			...result,
			slotGameBase: {
				...result.slotGameBase,
				paylines: result.slotGameBase.paylines.filter(({ type }) => type !== PaylineType.Scatter),
			},
			...feature,
		};
	}

	private extractFGTriggerState(
		slotOriginal: INrgBraggSlotOriginal | undefined,
		selectedBet: number,
		uncoverTier: RevealUncoverTier
	): IGameNetworkState["fgTriggerState"] {
		if (!slotOriginal?.slotFace) {
			return undefined;
		}

		const reveals = this.getSlotOriginalReveals(slotOriginal, selectedBet);
		return {
			reels: slotOriginal.slotFace.map((reel) => reel.map((symbolId) => symbolTypeBraggIds.get(symbolId) as string)),
			reveal: parseRevealBraggNetworkState(reveals, symbolTypeBraggIds, uncoverTier),
			nudging: nrgGenerateBraggNudgingOffsets(reveals),
		};
	}

	private getSlotOriginalReveals(slotOriginal: INrgBraggSlotOriginal, selectedBet: number): ILevelDataBragg["reveals"] {
		const levelsData = slotOriginal.levelData;
		if (!levelsData) {
			return [];
		}

		const selectedLevel = levelsData[String(selectedBet)];
		if (selectedLevel?.reveals) {
			return selectedLevel.reveals;
		}

		const fallbackLevel = Object.values(levelsData).find((level) => level?.reveals);
		return fallbackLevel?.reveals ?? [];
	}
}
